import React, { useState, useEffect } from 'react';
import { Upload, Download, Trash2, RefreshCw } from 'lucide-react';
import { PackagesTable } from '../components/packages/PackagesTable';
import { UploadPackagesModal } from '../components/packages/UploadPackagesModal';
import { UpdatePackagesModal } from '../components/packages/UpdatePackagesModal';
import { EditPackageModal } from '../components/packages/EditPackageModal';
import { generatePackageTemplate } from '../utils/packageValidation';
import { exportPackagesToExcel } from '../utils/exportPackages';
import { PackageSchema } from '../types/package';
import { usePackagesStore } from '../stores/packagesStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';

export const PackagesPage = () => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState<string | null>(null);
  const { translations } = useLanguage();  // Access translations from the context
  const { packages, loading, error, fetchPackages, uploadPackages, updatePackage, deleteAll } = usePackagesStore();

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleDownloadTemplate = () => {
    const template = generatePackageTemplate();
    const ws = XLSX.utils.json_to_sheet([template]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "packages-template.xlsx");
  };

  const handleUpload = async (data: PackageSchema[]) => {
    try {
      const result = await uploadPackages(data);
      return { message: result.message };
    } catch (error: any) {
      throw new Error(error.message || translations?.packagesPage?.uploadError || 'An error occurred while uploading data');
    }
  };

  const handleEditPackage = (packageId: string) => {
    setEditingPackageId(packageId);
    setIsEditModalOpen(true);
  };

  const handleSavePackage = async (packageData: any) => {
    try {
      if (!editingPackageId) return;
      
      await updatePackage(editingPackageId, packageData);
      toast.success(translations?.packagesPage?.updateSuccess || 'Package updated successfully');
      setEditingPackageId(null);
    } catch (error: any) {
      console.error('Error updating package:', error);
      toast.error(error.message || 'Failed to update package');
      throw error;
    }
  };

  const handleUpdate = async (data: PackageSchema[]) => {
    try {
      // Bulk update
      const updatePromises = data.map(async (packageData) => {
        // Find the package with matching name
        const existingPackage = Object.values(packages).find(pkg => pkg.name === packageData.name);
        
        if (existingPackage) {
          await updatePackage(existingPackage.id, packageData);
          return existingPackage.name;
        }
        return null;
      });

      const updatedPackages = (await Promise.all(updatePromises)).filter(Boolean);
      
      if (updatedPackages.length > 0) {
        toast.success(`Successfully updated ${updatedPackages.length} packages`);
      } else {
        toast.warning('No packages were found to update');
      }
      
      return { 
        message: updatedPackages.length > 0 
          ? `Successfully updated ${updatedPackages.length} packages` 
          : 'No packages were found to update'
      };
    } catch (error: any) {
      console.error('Error updating packages:', error);
      toast.error(error.message || 'Failed to update packages');
      throw new Error(error.message || translations?.packagesPage?.updateError || 'An error occurred while updating packages');
    }
  };

  const handleExport = (packagesToExport: Package[]) => {
    const success = exportPackagesToExcel(packagesToExport);
    if (success) {
      toast.success(translations?.packagesPage?.exportSuccess || 'Packages exported successfully');
    } else {
      toast.error(translations?.packagesPage?.exportError || 'Failed to export packages');
    }
  };

  const handleDeleteAll = async () => {
    try {
      await deleteAll();
      toast.success(translations?.packagesPage?.deleteSuccess || 'All packages have been deleted successfully');
    } catch (error: any) {
      toast.error(error.message || translations?.packagesPage?.deleteError || 'An error occurred while deleting packages');
    }
    setIsDeleteConfirmOpen(false);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">
          {translations?.packagesPage?.header || 'Hajj Packages'}
        </h1>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleDownloadTemplate}
            className="bg-primary-50 text-primary-600 px-4 py-2 rounded-lg hover:bg-primary-100 
                     transition-colors flex items-center gap-2"
          >
            <Download size={20} />
            <span>{translations?.packagesPage?.downloadTemplate || 'Download Template'}</span>
          </button>
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 
                     transition-colors flex items-center gap-2"
          >
            <Upload size={20} />
            <span>{translations?.packagesPage?.addPackages || 'Add Packages'}</span>
          </button>
          <button
            onClick={() => {
              setEditingPackageId(null);
              setIsUpdateModalOpen(true);
            }}
            className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 
                     transition-colors flex items-center gap-2"
          >
            <RefreshCw size={20} />
            <span>{translations?.packagesPage?.updatePackages || 'Update Packages'}</span>
          </button>
          <button
            onClick={() => setIsDeleteConfirmOpen(true)}
            className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 
                     transition-colors flex items-center gap-2"
          >
            <Trash2 size={20} />
            <span>{translations?.packagesPage?.deleteAll || 'Delete All Packages'}</span>
          </button>
        </div>
      </div>

      <PackagesTable 
        packages={packages}
        onEditPackage={handleEditPackage} 
      />
      
      <UploadPackagesModal 
        isOpen={isUploadModalOpen} 
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUpload}
      />

      <UpdatePackagesModal 
        isOpen={isUpdateModalOpen} 
        onClose={() => setIsUpdateModalOpen(false)}
        onUpdate={handleUpdate}
      />

      {/* Edit Package Modal for single package editing */}
      {editingPackageId && packages[editingPackageId] && (
        <EditPackageModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingPackageId(null);
          }}
          onSave={handleSavePackage}
          packageToEdit={packages[editingPackageId]}
        />
      )}

      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteAll}
        title={translations?.packagesPage?.deleteAllTitle || 'Delete All Packages'}
        message={translations?.packagesPage?.deleteAllMessage || 'Are you sure you want to delete all packages? This action cannot be undone.'}
        type="danger"
        confirmText={translations?.packagesPage?.confirmDelete || 'Delete All'}
        cancelText={translations?.packagesPage?.cancel || 'Cancel'}
      />
    </div>
  );
};